//////////////////
// IMPORTS
//////////////////

// libraries
import React, { useState, useEffect, useCallback } from "react";
import { Match, useRouter } from "found";

// queries, mutations, and types.
import { useQuery, useMutation } from "@apollo/client";
import {
  deleteMeetingSettings,
  DeleteMeetingSettings,
  DeleteMeetingSettingsVariables,
  MeetingSettings,
  MeetingSettingsQueryVariables,
  MeetingSettingsQuery,
  linkSettingsQuery,
  createMeetingSettings,
  CreateMeetingSettings,
  CreateMeetingSettingsVariables,
  updateMeetingSettings,
  UpdateMeetingSettings,
  UpdateMeetingSettingsVariables,
} from "@clockwise/web-commons/src/ui/setup-scheduling-link";

// components
import { SetupSchedulingLink } from "@clockwise/web-commons/src/ui/setup-scheduling-link/setup-scheduling-link";

// util
import { getError, getValue } from "@clockwise/web-commons/src/util/errorable.util";
import { getRenderTimeZone } from "@util/time-zone.util";
import { paths } from "@clockwise/client-commons/src/constants/site";
import { getSchedulingLinkPath } from "@clockwise/web-commons/src/util/scheduling.util";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { getBulkExperiments } from "@util/experiment.util";
import { getCurrentOrg } from "@util/org.util";
import { RouteRenderer } from "@components/route-renderer";

interface SetupSchedulingLinkRendererProps {
  match: Match;
}

export const SetupSchedulingLinkRenderer = ({ match }: SetupSchedulingLinkRendererProps) => {
  const trackEvent = useTracking();
  const { router } = useRouter();
  const [linkName, setLinkName] = useState("");
  const [slug, setSlug] = useState(match?.params?.["slug"] || "");
  const [isInvalidLink, setIsInvalidLink] = useState(false); // True when the linkName and slug don't return a link.
  const [createUpdateError, setCreateUpdateError] = useState<string | null>(null);
  const [linkSettingsData, setLinkSettingsData] = useState<MeetingSettings | null>(null);
  const [gqlError, setGqlError] = useState<boolean>(false); // Marks GraphEntityError found on the response data.

  const creatingNewLink = window.location.href.includes(paths.createSchedulingLink);
  const [createdNewLink, setCreatedNewLink] = useState(false);
  const onReturnToDashboard = () => router.push(paths.schedulingLinks);
  const goToLinkView = (linkName: string, slug: string) =>
    router.push(getSchedulingLinkPath(linkName, slug));
  const onViewLink = useCallback(() => goToLinkView(linkName, slug), [
    linkName,
    slug,
    goToLinkView,
  ]);

  // If editing an existing link, fetch the link data.
  const { loading, data, error } = useQuery<MeetingSettingsQuery, MeetingSettingsQueryVariables>(
    linkSettingsQuery,
    {
      fetchPolicy: "network-only",
      variables: {
        slug,
      },
      onError: (error) => {
        if (!creatingNewLink && error.message.includes?.("No such scheduling link")) {
          setIsInvalidLink(true);
        }
      },
      onCompleted: (data) => {
        const newMeetingSettings = getValue(data?.viewer?.user?.meetingSettingsErrorable);
        if (newMeetingSettings) {
          setLinkSettingsData(newMeetingSettings);
          setGqlError(false);
        } else {
          setGqlError(true);
        }

        // Get linkName.
        const newLinkName = getValue(data?.viewer?.user?.linkNameErrorable);
        if (newLinkName?.linkName) {
          setLinkName(newLinkName.linkName);
        }
      },
    },
  );

  // Keep the document title in sync with the link name.
  useEffect(() => {
    let docTitle = "Clockwise Links";
    if (creatingNewLink) {
      docTitle = "Create Clockwise Link";
    } else if (linkSettingsData && linkSettingsData.name) {
      docTitle = `Edit ${linkSettingsData.name}`;
    }

    // TODO(cw-shane) figure out what is setting the document.title before this hook runs. Why this setTimeout is needed.
    const timeout = setTimeout(() => {
      document.title = docTitle;
    }, 100); // Something is setting document.title again and overriding this shortly after this component mounts,
    // so waiting a moment before setting the title.

    return () => {
      clearTimeout(timeout);
    };
  }, [linkSettingsData]);

  const [createLink] = useMutation<CreateMeetingSettings, CreateMeetingSettingsVariables>(
    createMeetingSettings,
    {
      fetchPolicy: "no-cache", // Without this, Apollo refetches the MeetingSettings object.
      onError: () => {
        setCreateUpdateError("Problem creating Link, please try again.");
      },
      onCompleted: (data) => {
        const newMeetingSettings = getValue(data?.createMeetingSettings?.meetingSettingsErrorable);

        if (newMeetingSettings) {
          if (!linkSettingsData) {
            setCreatedNewLink(true);
          }
          setLinkSettingsData(newMeetingSettings);
          newMeetingSettings?.slug && setSlug(newMeetingSettings.slug);
          onReturnToDashboard();
        }
      },
    },
  );

  const [updateLink] = useMutation<UpdateMeetingSettings, UpdateMeetingSettingsVariables>(
    updateMeetingSettings,
    {
      fetchPolicy: "no-cache", // Without this, Apollo refetches the MeetingSettings object.
      onError: () =>
        setCreateUpdateError("Something went wrong when updating the meeting settings."),
      onCompleted: (data) => {
        const newMeetingSettings = getValue(data.updateMeetingSettings?.meetingSettingsErrorable);
        const error = getError(data.updateMeetingSettings?.meetingSettingsErrorable);

        if (newMeetingSettings) {
          if (!linkSettingsData) {
            setCreatedNewLink(true);
          }
          setLinkSettingsData(newMeetingSettings);
          newMeetingSettings?.slug && setSlug(newMeetingSettings.slug);
          newMeetingSettings.isActive
            ? goToLinkView(linkName, newMeetingSettings.slug)
            : onReturnToDashboard();
        }
        if (error) {
          setCreateUpdateError(error.message ?? null);
        }
      },
    },
  );

  // Convert the newLinkSettings to the mutation input type, CreateMeetingSettingsVariables.
  const createOrUpdateLinkWrapped = useCallback(
    async (newLinkSettings: MeetingSettings) => {
      if (newLinkSettings.id) {
        await updateLink({
          variables: {
            input: {
              body: newLinkSettings,
            },
          },
        });
        trackEvent(TrackingEvents.LINKS.EDIT_PAGE.SAVE_CHANGES, { newLinkSettings });
      } else {
        await createLink({
          variables: {
            input: {
              body: newLinkSettings,
            },
          },
        });
        trackEvent(TrackingEvents.LINKS.EDIT_PAGE.CREATE, { newLinkSettings });
      }
    },
    [updateLink, createLink],
  );

  const [deleteLink, { loading: deletingLink }] = useMutation<
    DeleteMeetingSettings,
    DeleteMeetingSettingsVariables
  >(deleteMeetingSettings, {
    fetchPolicy: "no-cache", // Without this, Apollo refetches the MeetingSettings object.
    onCompleted: () => {
      setLinkSettingsData(null);
      onReturnToDashboard();
    },
  });

  const deleteLinkWrapped = useCallback(
    async (meetingSettingsId: string) => {
      await deleteLink({
        variables: {
          input: {
            meetingSettingsId,
          },
        },
      });
      trackEvent(TrackingEvents.LINKS.EDIT_PAGE.DELETE, { meetingSettingsId });
    },
    [deleteLink],
  );

  const showErrorScreen = !creatingNewLink && (error || gqlError || !data) && !loading;
  const org = getCurrentOrg(data?.viewer || null);
  const canUseZoom =
    !!getValue(org?.zoomUserLevelAccount) || !!getValue(org?.zoomAccount)?.hasValidZoomAccount;
  const experiments = getBulkExperiments(org);

  return (
    <RouteRenderer
      loading={loading && !linkName}
      error={showErrorScreen}
      notFound={isInvalidLink || !experiments.LinkScheduler}
    >
      <SetupSchedulingLink
        currentLinkSettings={linkSettingsData}
        canUseZoom={canUseZoom}
        timeZone={getRenderTimeZone()}
        onReturnToDashboard={onReturnToDashboard}
        onCreateUpdate={createOrUpdateLinkWrapped}
        onDelete={deleteLinkWrapped}
        createUpdateError={createUpdateError}
        saving={loading}
        linkName={linkName}
        createdNewLink={createdNewLink}
        deleting={deletingLink}
        clearError={() => setCreateUpdateError(null)}
        onPreview={onViewLink}
        givenName={data?.viewer?.user?.givenName}
        familyName={data?.viewer?.user?.familyName}
      />
    </RouteRenderer>
  );
};
